import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Cross, LogOut, Inbox, FileText, BookOpen, Users, Download,
  Phone, MessageCircle, Pencil, Trash2, Plus, Loader2,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import TestimonialsTab from "./TestimonialsTab";
import StudentsTab from "./StudentsTab";
import DonationsTab from "./DonationsTab";
import ChangePassword from "./ChangePassword";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const ENQUIRY_STATUSES = ["New", "Contacted", "Enrolled", "Closed"];
const APP_STATUSES = ["Submitted", "Reviewed", "Accepted", "Closed"];
const EMPTY_COURSE = { title: "", category: "teacher-training", tagline: "", description: "", duration: "", eligibility: "", fee: "", image: "", active: true };

const STATUS_COLORS = {
  New: "bg-terracotta/15 text-terracotta-dark",
  Contacted: "bg-amber-100 text-amber-800",
  Enrolled: "bg-forest/10 text-forest",
  Closed: "bg-stone-200 text-stone-600",
  Submitted: "bg-terracotta/15 text-terracotta-dark",
  Reviewed: "bg-amber-100 text-amber-800",
  Accepted: "bg-forest/10 text-forest",
};

function StatusSelect({ value, options, onChange, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${STATUS_COLORS[value] || "bg-stone-100 text-stone-600"} focus:outline-none`}
    >
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [courseDialog, setCourseDialog] = useState(null); // null | {mode, data}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, e, a, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries"),
        api.get("/admin/applications"),
        api.get("/admin/courses"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setApplications(a.data);
      setCourses(c.data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load dashboard data."));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const filteredEnquiries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enquiries.filter(
      (e) =>
        (statusFilter === "All" || e.status === statusFilter) &&
        (!q || e.name?.toLowerCase().includes(q) || e.phone?.includes(q) || e.course?.toLowerCase().includes(q)),
    );
  }, [enquiries, statusFilter, search]);

  const updateEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setApplications((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCourse = async (course, active) => {
    try {
      await api.put(`/admin/courses/${course.id}`, { ...course, active });
      setCourses((list) => list.map((c) => (c.id === course.id ? { ...c, active } : c)));
      toast.success(active ? "Course activated" : "Course hidden from website");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCourse = async () => {
    const { mode, data } = courseDialog;
    if (!data.title.trim()) {
      toast.error("Course title is required.");
      return;
    }
    setSaving(true);
    try {
      if (mode === "add") {
        const r = await api.post("/admin/courses", data);
        setCourses((list) => [...list, r.data]);
      } else {
        await api.put(`/admin/courses/${data.id}`, data);
        setCourses((list) => list.map((c) => (c.id === data.id ? data : c)));
      }
      toast.success(mode === "add" ? "Course added" : "Course updated");
      setCourseDialog(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async (id) => {
    try {
      await api.delete(`/admin/courses/${id}`);
      setCourses((list) => list.filter((c) => c.id !== id));
      toast.success("Course deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Phone", "Email", "Course", "Status", "Message", "Date"]];
    filteredEnquiries.forEach((e) =>
      rows.push([e.name, e.phone, e.email || "", e.course, e.status, (e.message || "").replace(/[\n,]+/g, " "), e.created_at?.slice(0, 10) || ""]),
    );
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const STAT_CARDS = [
    { icon: Inbox, label: "Total Enquiries", value: stats?.total_enquiries ?? "—", testid: "stat-total-enquiries" },
    { icon: Users, label: "New (Unread)", value: stats?.new_enquiries ?? "—", testid: "stat-new-enquiries" },
    { icon: FileText, label: "Applications", value: stats?.applications ?? "—", testid: "stat-applications" },
    { icon: BookOpen, label: "Active Courses", value: stats?.active_courses ?? "—", testid: "stat-active-courses" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 border-b border-forest/10 bg-cream/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest text-cream">
              <Cross className="h-4 w-4" />
            </span>
            <div>
              <p className="font-serif text-lg font-bold leading-none text-forest-deep">Admin Command Center</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">{user.name || user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ChangePassword />
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-button" className="rounded-full border-forest/20 font-display text-forest hover:bg-forest hover:text-cream">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {STAT_CARDS.map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-3xl border border-forest/10 bg-white p-6">
              <s.icon className="h-5 w-5 text-terracotta" />
              <p className="mt-4 font-serif text-4xl font-bold text-forest-deep">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.14em] text-stone-500">{s.label}</p>
            </div>
          ))}
        </div>

        {stats?.popular_courses?.length > 0 && (
          <div className="mt-4 rounded-3xl border border-forest/10 bg-white p-6" data-testid="popular-courses-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Most enquired this period</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {stats.popular_courses.map((p) => (
                <span key={p.course} className="rounded-full bg-forest/10 px-4 py-1.5 text-xs font-semibold text-forest">
                  {p.course} · {p.count}
                </span>
              ))}
            </div>
          </div>
        )}

        <Tabs defaultValue="enquiries" className="mt-10" data-testid="admin-tabs">
          <TabsList className="h-auto flex-wrap bg-linen" data-testid="admin-tabs-list">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">Courses ({courses.length})</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone or course..."
                data-testid="enquiry-search-input"
                className="h-10 max-w-xs rounded-full bg-white"
              />
              <div className="flex flex-wrap gap-2" data-testid="enquiry-status-filters">
                {["All", ...ENQUIRY_STATUSES].map((s) => (
                  <button
                    key={s}
                    data-testid={`enquiry-filter-${s.toLowerCase()}`}
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                      statusFilter === s ? "bg-forest text-cream" : "border border-forest/20 bg-white text-forest"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-button" className="ml-auto rounded-full border-forest/20 font-display text-forest">
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </div>

            <div className="mt-6 space-y-3" data-testid="enquiries-list">
              {filteredEnquiries.map((e) => (
                <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display font-semibold text-forest-deep">{e.name}</p>
                      <span className="rounded-full bg-linen px-3 py-0.5 text-[11px] font-medium text-forest">{e.course}</span>
                    </div>
                    <p className="mt-1 text-sm text-stone-500">
                      {e.phone} {e.email ? `· ${e.email}` : ""} · {e.created_at?.slice(0, 10)}
                    </p>
                    {e.message && <p className="mt-1.5 text-sm italic text-stone-600">"{e.message}"</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <a href={`tel:${e.phone}`} data-testid={`enquiry-call-${e.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="Call">
                      <Phone className="h-4 w-4" />
                    </a>
                    <a href={`https://wa.me/${e.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid={`enquiry-whatsapp-${e.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="WhatsApp">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                    <StatusSelect value={e.status} options={ENQUIRY_STATUSES} onChange={(s) => updateEnquiryStatus(e.id, s)} testid={`enquiry-status-${e.id}`} />
                  </div>
                </div>
              ))}
              {filteredEnquiries.length === 0 && (
                <p className="rounded-2xl border border-dashed border-forest/20 bg-white py-14 text-center text-sm text-stone-500" data-testid="enquiries-empty">
                  No enquiries match your filters yet.
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <div className="space-y-3" data-testid="applications-list">
              {applications.map((a) => (
                <div key={a.id} data-testid={`application-row-${a.id}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display font-semibold text-forest-deep">{a.name}</p>
                      <span className="rounded-full bg-linen px-3 py-0.5 text-[11px] font-medium text-forest">{a.course}</span>
                    </div>
                    <p className="mt-1 text-sm text-stone-500">
                      {a.phone} {a.email ? `· ${a.email}` : ""}
                      {a.education ? ` · ${a.education}` : ""} {a.batch ? ` · ${a.batch}` : ""} · {a.created_at?.slice(0, 10)}
                    </p>
                    {a.message && <p className="mt-1.5 text-sm italic text-stone-600">"{a.message}"</p>}
                  </div>
                  <StatusSelect value={a.status} options={APP_STATUSES} onChange={(s) => updateApplicationStatus(a.id, s)} testid={`application-status-${a.id}`} />
                </div>
              ))}
              {applications.length === 0 && (
                <p className="rounded-2xl border border-dashed border-forest/20 bg-white py-14 text-center text-sm text-stone-500" data-testid="applications-empty">
                  No applications received yet.
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="courses" className="mt-6">
            <div className="flex justify-end">
              <Button onClick={() => setCourseDialog({ mode: "add", data: { ...EMPTY_COURSE } })} data-testid="add-course-button" className="rounded-full bg-forest font-display text-cream hover:bg-forest-deep">
                <Plus className="mr-2 h-4 w-4" /> Add Course
              </Button>
            </div>
            <div className="mt-4 space-y-3" data-testid="courses-list">
              {courses.map((c) => (
                <div key={c.id} data-testid={`admin-course-row-${c.slug}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold text-forest-deep">{c.title}</p>
                    <p className="mt-1 text-sm text-stone-500">
                      {c.duration} · {c.eligibility} · {c.fee}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-stone-500">
                      <Switch checked={c.active} onCheckedChange={(v) => toggleCourse(c, v)} data-testid={`course-active-${c.slug}`} />
                      {c.active ? "Live" : "Hidden"}
                    </label>
                    <button onClick={() => setCourseDialog({ mode: "edit", data: { ...c } })} data-testid={`course-edit-${c.slug}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="Edit course">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteCourse(c.id)} data-testid={`course-delete-${c.slug}`} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-600 hover:text-white" aria-label="Delete course">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="testimonials" className="mt-6">
            <TestimonialsTab />
          </TabsContent>
          <TabsContent value="students" className="mt-6">
            <StudentsTab />
          </TabsContent>
          <TabsContent value="donations" className="mt-6">
            <DonationsTab />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!courseDialog} onOpenChange={(open) => !open && setCourseDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-lg" data-testid="course-dialog">
          {courseDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-forest-deep">
                  {courseDialog.mode === "add" ? "Add New Course" : "Edit Course"}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2 space-y-4">
                {[
                  ["title", "Title", "e.g. Nursery Teacher Training (NTT)"],
                  ["tagline", "Tagline", "Short one-line hook"],
                  ["duration", "Duration", "e.g. 12 Months"],
                  ["eligibility", "Eligibility", "e.g. SSLC / 10th Pass"],
                  ["fee", "Fee", "e.g. On enquiry"],
                  ["image", "Image URL", "https://..."],
                ].map(([key, label, ph]) => (
                  <div key={key}>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">{label}</label>
                    <Input
                      value={courseDialog.data[key] || ""}
                      onChange={(e) => setCourseDialog((d) => ({ ...d, data: { ...d.data, [key]: e.target.value } }))}
                      placeholder={ph}
                      data-testid={`course-form-${key}`}
                      className="mt-1.5 bg-white"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Category</label>
                  <select
                    value={courseDialog.data.category}
                    onChange={(e) => setCourseDialog((d) => ({ ...d, data: { ...d.data, category: e.target.value } }))}
                    data-testid="course-form-category"
                    className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                  >
                    <option value="teacher-training">Teacher Training</option>
                    <option value="language">Language & IELTS</option>
                    <option value="vocational">Vocational</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Description</label>
                  <textarea
                    value={courseDialog.data.description || ""}
                    onChange={(e) => setCourseDialog((d) => ({ ...d, data: { ...d.data, description: e.target.value } }))}
                    data-testid="course-form-description"
                    className="mt-1.5 min-h-[90px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  />
                </div>
                <Button onClick={saveCourse} disabled={saving} data-testid="course-save-button" className="w-full rounded-full bg-forest py-5 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
                  {saving ? "Saving..." : "Save Course"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
